import { fabric } from 'fabric';
import { calculateFabricCoordsCenter } from './calculate-fabric-coords-center';
import { checkIfImageIntersectsPhotozoneEdge } from './check-if-image-intersects-edge';
import { shrinkPt } from './shrink-pt';
import { checkIfImageIsOverlapping } from './check-if-image-is-overlapping';

/**
 * Adds a movement constraint to a photozone image so it cannot be dragged outside its photozone bounds.
 * @param image The fabric.Image to constrain
 * @param photoZone The fabric.Object representing the photozone
 */
export function addPhotozoneImageMoveConstraint(image: fabric.Image, photoZone: fabric.Object) {
  // Tracks the last valid position of the image (where it was still overlapping the photozone)
  let lastValidPosition = { left: image.left ?? 0, top: image.top ?? 0 };

  /**
   * Main constraint callback: ensures the image always overlaps the photozone.
   * - Allows the image to move/rotate/scale as long as it overlaps the photozone (even if all corners are outside).
   * - If the image is moved completely outside (no overlap), reverts to the last valid position.
   * - Also reverts if any edge of the image intersects the photozone margin (prevents crossing boundary).
   */
  const onCallback = () => {
    image.setCoords();
    photoZone.setCoords();

    // Get the four corners of the image and photozone (with margin)
    const imageCoords = image.getCoords();
    const photoZoneCoords = photoZone.getCoords();
    const center = calculateFabricCoordsCenter(photoZoneCoords);
    const photoZoneMarginCoords = (photoZoneCoords as Array<{ x: number; y: number }>).map((coord) =>
      shrinkPt({ pt: coord, center }),
    );

    // Check if any edge of the image intersects any edge of the photozone (with margin)
    const anyEdgeIntersect = checkIfImageIntersectsPhotozoneEdge({
      imageCoords,
      photoZoneMarginCoords: photoZoneMarginCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
    });

    // Check if the image is inside the photozone
    const isOverlapping = checkIfImageIsOverlapping({
      imageCoords,
      photoZoneMarginCoords: photoZoneMarginCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
    });

    // If the image is completely outside the photozone (no overlap), or if it crosses the boundary, revert to last valid position
    if (!isOverlapping || anyEdgeIntersect) {
      image.set({ left: lastValidPosition.left, top: lastValidPosition.top });
      image.setCoords();
    } else {
      // Otherwise, update last valid position
      lastValidPosition = { left: image.left ?? 0, top: image.top ?? 0 };
    }
  };

  image.on('moving', () => {
    onCallback();
  });

  return {
    onCallback,
  };
}
